import { makeActions } from '@redux/crudCreator/actions';
import {
  PRIMARY_KEY,
  convertRequestParams,
  convertResponseData,
} from '@redux/crudCreator/dataProvider';
import { apiWrapper } from '@redux/crudCreator/reduxUtils';
import { CreatePayloadType, GetAllPayloadType } from '@redux/crudCreator/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAllApi } from 'api/crud';
import {
  calculateShippingFeeApi,
  confirmForcePaymentApi,
  getOrderByIdApi,
  getOriginalOrderApi,
} from 'api/orders';
import {
  cancelOrdersExchangeApi,
  changePaymentToBankApi,
  confirmOrderExchangeApi,
} from 'api/ordersExchange';
import { flatten, uniq } from 'lodash';
import { DynamicObjectType } from 'types/commons';

export const MODEL_NAME = 'ordersExchange';
export const ordersExchangeActions = makeActions(
  MODEL_NAME,
  'id',
  'orders-exchange',
);

export const getAllOrdersExchange = ordersExchangeActions.getAll;
export const editOrdersExchange = ordersExchangeActions.edit;
export const createOrdersExchange = ordersExchangeActions.create;
export const getByIdOrdersExchange = ordersExchangeActions.getDataById;

export const getOriginalOrder = createAsyncThunk(
  'ordersExchange/getOriginalOrder',
  async (payload: CreatePayloadType, thunkAPI) => {
    const { data, options } = payload;
    try {
      const response = await apiWrapper(
        {
          isShowSuccessNoti: false,
          ...options,
        },
        getOriginalOrderApi,
        data,
      );
      if (response) {
        return response;
      }
      return thunkAPI.rejectWithValue({ error: true, data: response });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const calculateShippingFee = createAsyncThunk(
  'ordersExchange/calculateShippingFee',
  async (payload: CreatePayloadType, thunkAPI) => {
    const { data } = payload;
    try {
      const response = await apiWrapper(
        {
          isShowSuccessNoti: false,
        },
        calculateShippingFeeApi,
        data,
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getOrderWithVariantsById = createAsyncThunk(
  'ordersExchange/getOrderWithVariantsById',
  async (payload: GetAllPayloadType, thunkAPI) => {
    const { data = {}, options = {} } = payload;
    const { pageSize, page, includes } = thunkAPI.getState()[MODEL_NAME];
    try {
      const orderDetail = await getOrderByIdApi(data?.id);
      const convertRequest = convertRequestParams('GET_ALL', {
        limit: pageSize,
        offset: pageSize * (page - 1),
        includes,
        filters: [
          {
            condition: 'and',
            key: 'id',
            operator: 'is',
            value: flatten(
              orderDetail?.items?.map((e) => [
                e,
                ...(e?.product?.comboItems || []),
              ]),
            )?.map(
              (e: DynamicObjectType) => e?.product?.variantId || e.variantId,
            ),
          },
        ],
        join: ['detail||name,langCode'],
        pageSize: 100,
      });
      const response = await apiWrapper(
        { isShowProgress: options.isShowProgress },
        getAllApi,
        'product-variants',
        convertRequest,
      );

      const listAttributeValueIds = uniq(
        flatten(
          response?.data
            ?.map((item) => item?.attributeValueIds)
            .filter((e) => e),
        ),
      ) as string[];

      const convertRequestAttributeValues = convertRequestParams('GET_ALL', {
        limit: 100,
        offset: 0,
        join: ['detail'],
        filters: listAttributeValueIds?.length > 0 && [
          {
            condition: 'and',
            key: 'id',
            operator: 'is',
            value: listAttributeValueIds || undefined,
          },
        ],
      });

      const attributeValuesRes =
        listAttributeValueIds?.length > 0 &&
        (await apiWrapper(
          { isShowProgress: options.isShowProgress },
          getAllApi,
          'product-attribute-values',
          convertRequestAttributeValues,
        ));

      const formatResponse = response?.data?.map((e) => {
        const attributeValues = attributeValuesRes?.data?.filter((item) =>
          e?.attributeValueIds?.includes(item.id),
        );
        return {
          ...e,
          attributeValues,
        };
      });

      const result = convertResponseData(
        'GET_ALL',
        { ...response, data: formatResponse },
        {
          primaryKey: PRIMARY_KEY,
        },
      );

      const decayElements = flatten(
        orderDetail.items.map((item) => {
          if (item?.quantity <= 1) {
            return {
              ...item?.variant,
              // name: getDataDetail(item, 'detail', 'name', null)?.name,
              name: item?.productName,
              discountPrice: item?.discountPrice || 0,
              originalPrice: item?.originalPrice || 0,
              quantity: 1,
              variantId: item?.variant?.id || item?.variantId,
            };
          }

          const dummy = new Array(item?.quantity).fill({
            ...item?.variant,
            // name: getDataDetail(item, 'detail', 'name', null)?.name,
            name: item?.productName,
            discountPrice: item?.discountPrice || 0,
            originalPrice: item?.originalPrice || 0,
            quantity: 1,
            variantId: item?.variant?.id || item?.variantId,
          });

          return dummy;
        }),
      ).map((item: DynamicObjectType, index) => ({
        ...item,
        keyPosition: `${item?.id}-${(index + 1).toString()}`,
      }));

      if (
        result.data &&
        Array.isArray(decayElements) &&
        decayElements?.length > 0
      ) {
        return {
          data: {
            numberOfPages: Math.round(result.total / pageSize),
            ...orderDetail,
            items: decayElements?.map((item: DynamicObjectType) => {
              return {
                ...item,
                variant: result.data[item?.variantId],
              };
            }),
          },
          options,
        };
      }

      return thunkAPI.rejectWithValue({
        data: { ...orderDetail, ...response },
        options,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue({ data, options });
    }
  },
);

export const cancelOrdersExchange = createAsyncThunk(
  'ordersExchange/cancelOrdersExchange',
  async (payload: CreatePayloadType, thunkAPI) => {
    const { data, options } = payload;
    try {
      const response = await apiWrapper(
        { isShowSuccessNoti: options.isShowSuccessNoti },
        cancelOrdersExchangeApi,
        data,
      );
      if (response) {
        return {
          ...response,
          ...payload,
          success: true,
        };
      }
      return thunkAPI.rejectWithValue({ error: true, data: response });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const confirmOrderExchange = createAsyncThunk(
  'ordersExchange/confirmOrderExchange',
  async (payload: CreatePayloadType, thunkAPI) => {
    const { data, options } = payload;
    try {
      const response = await apiWrapper(options, confirmOrderExchangeApi, data);
      if (response) {
        return { data: response, success: true };
      }
      return thunkAPI.rejectWithValue({ error: true, data: response });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const confirmForcePayment = createAsyncThunk(
  'orders/confirmOrder',
  async (payload: any, thunkAPI) => {
    const { data, options } = payload;
    try {
      const response = await apiWrapper(options, confirmForcePaymentApi, data);
      if (response) {
        thunkAPI.dispatch(
          getByIdOrdersExchange({
            data: {
              id: data?.exchangeId,
            },
            options: {
              isRequestApi: true,
              extraParams: {
                join: [
                  'customer',
                  'newOrder',
                  'newOrder.payments',
                  'newOrder.items',
                  'orderReturn',
                  'originalOrder',
                  'originalOrder.items',
                ],
              },
            },
          }),
        );
        return { data: response, success: true };
      }
      return thunkAPI.rejectWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const changePaymentToBank = createAsyncThunk(
  'orders/changePaymentToBank',
  async (payload: any, thunkAPI) => {
    const { data, options } = payload;
    try {
      const response = await apiWrapper(options, changePaymentToBankApi, data);
      if (response) {
        thunkAPI.dispatch(
          getByIdOrdersExchange({
            data: {
              id: data?.exchangeId,
            },
            options: {
              isRequestApi: true,
              extraParams: {
                join: [
                  'customer',
                  'newOrder',
                  'newOrder.payments',
                  'newOrder.items',
                  'orderReturn',
                  'originalOrder',
                  'originalOrder.items',
                ],
              },
            },
          }),
        );
        return { data: response, success: true };
      }
      return thunkAPI.rejectWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
